import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Container, Form, InputGroup, Pagination, Row, Spinner } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import { BsArrowRight, BsClock, BsCollection, BsGrid3X3Gap, BsSearch, BsStars, BsX } from 'react-icons/bs';
import { listPublicSets } from './publicSetsApi';
import './PublicSetGallery.css';

const NO_COVER = 'https://www.hearthcards.net/newsets/images/set-no-image.webp';
const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'updated', label: 'Recently updated' },
  { value: 'largest', label: 'Most sections' },
  { value: 'title', label: 'Title A–Z' },
];

const formatDate = (value) => {
  if (!value) return 'Unknown';
  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
};

const pageItems = (current, total) => {
  const pages = new Set([1, total, current - 1, current, current + 1]);
  return [...pages].filter((page) => page >= 1 && page <= total).sort((a, b) => a - b);
};
const ratingLabel = (value) => {
  const rating = Number(value);
  if (!Number.isFinite(rating) || rating < 1 || rating > 5) return null;
  return Number.isInteger(rating) ? String(rating) : rating.toFixed(1);
};

function SetCard({ set, onOpenCard }) {
  const rating = ratingLabel(set.rating);
  return (
    <div className="public-set-card-shell">
      <article
        className={`public-set-card${set.userCardId ? ' public-set-card--clickable' : ''}`}
        onClick={() => set.userCardId && onOpenCard(set.userCardId)}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget || !set.userCardId) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onOpenCard(set.userCardId);
          }
        }}
        role={set.userCardId ? 'button' : undefined}
        tabIndex={set.userCardId ? 0 : undefined}
        aria-label={set.userCardId ? `Open community card for ${set.title}` : undefined}
      >
        <img className="public-set-card-image" src={set.coverUrl || NO_COVER} alt="" loading="lazy" />
        <div className="public-set-card-shade" />
        <div className="public-set-card-content">
          <h2 title={set.title}>{set.title}</h2>
          <p className="public-set-card-creator">By {set.creatorName}</p>
          {/* {set.description && <p className="public-set-card-description">{set.description}</p>} */}
          <div className="public-set-card-meta">
            <span title={`${set.moduleCount} sections`}><BsGrid3X3Gap /> {set.moduleCount}</span>
            <span title={`${set.cardCount} referenced cards`}><BsCollection /> {set.cardCount}</span>
            <span title={`Updated ${formatDate(set.updatedAt)}`}><BsClock /> {formatDate(set.updatedAt)}</span>
          </div>
          <Link className="public-set-card-open" to={`/sets/${set.setId}`} onClick={(event) => event.stopPropagation()} aria-label={`Explore ${set.title}`}>Explore <BsArrowRight /></Link>
        </div>
      </article>
      {rating && <span className="public-set-card-rating" title={`Rating ${rating} out of 5`}>{rating}</span>}
    </div>
  );
}

export default function PublicSetGallery() {
  const [searchParams, setSearchParams] = useSearchParams();
  const querySearch = searchParams.get('search') || '';
  const querySort = SORT_OPTIONS.some(({ value }) => value === searchParams.get('sort')) ? searchParams.get('sort') : 'newest';
  const queryPage = Math.max(1, Number.parseInt(searchParams.get('page') || '1', 10) || 1);
  const [searchDraft, setSearchDraft] = useState(querySearch);
  const [state, setState] = useState({ loading: true, error: '', sets: [], pagination: null });

  useEffect(() => setSearchDraft(querySearch), [querySearch]);

  useEffect(() => {
    const controller = new AbortController();
    setState((current) => ({ ...current, loading: true, error: '' }));
    listPublicSets({ page: queryPage, sort: querySort, search: querySearch }, controller.signal)
      .then((payload) => setState({ loading: false, error: '', sets: payload.sets || [], pagination: payload.pagination || null }))
      .catch((error) => {
        if (error.name !== 'AbortError') setState((current) => ({ ...current, loading: false, error: error.message }));
      });
    return () => controller.abort();
  }, [queryPage, querySearch, querySort]);

  const updateQuery = (updates) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === '' || value === null || value === undefined || (key === 'page' && Number(value) === 1)) next.delete(key);
      else next.set(key, String(value));
    });
    setSearchParams(next);
  };

  const submitSearch = (event) => {
    event.preventDefault();
    updateQuery({ search: searchDraft.trim(), page: 1 });
  };

  const openCard = (userCardId) => updateQuery({ selectedCard: userCardId });

  const pages = useMemo(() => pageItems(state.pagination?.page || 1, state.pagination?.totalPages || 1), [state.pagination]);

  return (
    <main className="public-set-gallery p-3 p-md-5">
      <Container fluid="xxl">
        <header className="public-set-gallery-hero">
          <div>
            <span className="public-set-gallery-eyebrow">Community creations</span>
            <h1>Sets Gallery</h1>
          </div>
          {state.pagination && <div className="public-set-gallery-count"><strong>{state.pagination.total}</strong><span>public set{state.pagination.total === 1 ? '' : 's'}</span></div>}
        </header>

        <section className="public-set-gallery-toolbar" aria-label="Gallery filters">
          <Form onSubmit={submitSearch} className="public-set-gallery-search">
            <InputGroup>
              <InputGroup.Text><BsSearch /></InputGroup.Text>
              <Form.Control value={searchDraft} onChange={(event) => setSearchDraft(event.target.value)} maxLength={80} placeholder="Search sets or creators" aria-label="Search public sets" />
              {searchDraft && <Button type="button" variant="outline-secondary" onClick={() => { setSearchDraft(''); updateQuery({ search: '', page: 1 }); }} aria-label="Clear search"><BsX /></Button>}
              <Button type="submit" variant="primary">Search</Button>
            </InputGroup>
          </Form>
          <Form.Select value={querySort} onChange={(event) => updateQuery({ sort: event.target.value, page: 1 })} aria-label="Sort public sets">
            {SORT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </Form.Select>
        </section>

        {state.error && <Alert variant="danger">{state.error}</Alert>}

        {state.loading ? (
          <div className="public-set-gallery-state"><Spinner animation="border" /><span>Loading public sets...</span></div>
        ) : state.sets.length ? (
          <Row className="g-3 g-lg-4">
            {state.sets.map((set) => <Col className="public-set-gallery-col" key={set.setId} xs={6} md={4}><SetCard set={set} onOpenCard={openCard} /></Col>)}
          </Row>
        ) : (
          <div className="public-set-gallery-state"><BsSearch /><h2>No public sets found</h2><p>Try another search, or check back when creators publish something new.</p></div>
        )}

        {!state.loading && (state.pagination?.totalPages || 1) > 1 && (
          <Pagination className="public-set-gallery-pagination" aria-label="Public set gallery pages">
            <Pagination.Prev disabled={state.pagination.page <= 1} onClick={() => updateQuery({ page: state.pagination.page - 1 })} />
            {pages.map((page, index) => (
              <React.Fragment key={page}>
                {index > 0 && page - pages[index - 1] > 1 && <Pagination.Ellipsis disabled />}
                <Pagination.Item active={page === state.pagination.page} onClick={() => updateQuery({ page })}>{page}</Pagination.Item>
              </React.Fragment>
            ))}
            <Pagination.Next disabled={state.pagination.page >= state.pagination.totalPages} onClick={() => updateQuery({ page: state.pagination.page + 1 })} />
          </Pagination>
        )}
      </Container>
    </main>
  );
}
